import _ from 'lodash';
import FormElement from './FormElement';
import StyleMixin from './mixins/styleMixins/StyleMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import DisabledMixin from './mixins/DisabledMixin';
import FocusMixin from './mixins/FocusMixin';
import mixin from '../privates/utils/mixin';
import * as members from '../privates/privateMembers';
import * as postMessage from '../privates/postMessage';
import platformUtils from '../privates/platformUtils';
import imageUtils from '../privates/utils/imageUtils';
import videoUtils from '../privates/utils/videoUtils';
import audioUtils from '../privates/utils/audioUtils';
import documentUtils from '../privates/utils/documentUtils';
import {validate} from '../privates/validations/validation';
import {emptyStringIfNil} from '../privates/utils';
import reporter from '../privates/reporter';
import {generateError} from '../privates/validations/errors';
import {runValidationsAndSync, hideValidityIndication} from '../privates/validationsService/validationsService';
import RequiredMixin from './mixins/RequiredMixin';
import {setStageUnfriendlyFunctions} from '../privates/utils/livePreview';

/**
 * @typedef {Object} $w.UploadButton~File
 * @summary The object used by the [`value`](#value) property that represents files ready for upload.
 * @property {String} name The file's name.
 * @property {String} size The file's size in bytes.
 * @snippet [UploadButton-value.es6=Get the files pending upload]
 * @see [value](#value)
 */

/**
 * @typedef {Object} $w.UploadButton~UploadedFile
 * @summary The object returned by the `startUpload()`'s Promise.
 * @property {String} url The Wix URL of the successfully uploaded file.
 * @property {String} mediaId The Wix media ID of the uploaded file.
 * @property {String} title The title of the uploaded file.
 * @property {Number} width The width of an uploaded image or video file.
 * @property {Number} height The height of an uploaded image or video file.
 * @property {Number} duration The duration (in milliseconds) of an uploaded audio file.
 * @snippet [UploadButton-startUpload.es6=Get uploaded file information]
 * @see [startUpload](#startUpload)
 */

/**
 * @typedef {Object} $w.UploadButton~UploadError
 * @summary The error message object returned when the `startUpload()`'s Promise is rejected.
 * @property {String} errorCode The error's code.
 * @property {String} errorDescription The error's description.
 * @snippet [UploadButton-startUpload.es6=Get upload error information]
 * @see [startUpload](#startUpload)
 */


function getWixCodeURI(fileInfo) {
    const uriUtils = platformUtils.getUriUtils();
    const mediaType = fileInfo.media_type;
    switch (mediaType) {
        case 'picture':
            return uriUtils.generateWixCodeImageUri(fileInfo);
        case 'music':
            return uriUtils.generateWixCodeAudioUri(fileInfo);
        case 'video':
            return uriUtils.generateWixCodeVideoUri(fileInfo);
        case 'document':
            return uriUtils.generateWixCodeDocumentUri(fileInfo);
        default:
            throw new TypeError(`Unknown media_type "${mediaType}"`);
    }
}

function getFileInfo(mediaType, url) {
    switch (mediaType) {
        case 'picture':
            const imageData = imageUtils.extractModelDataFromSrc(url);
            return {
                url,
                mediaId: imageData.uri,
                title: imageData.title,
                width: imageData.width,
                height: imageData.height
            };
        case 'document':
            const documentData = documentUtils.extractModelDataFromSrc(url);
            return {
                url,
                mediaId: documentData.mediaId,
                title: documentData.title
            };
        case 'video':
            const videoData = videoUtils.extractModelDataFromSrc(url);
            return {
                url,
                mediaId: videoData.videoId,
                title: videoData.fileName,
                width: videoData.posterImageRef.width,
                height: videoData.posterImageRef.height
            };
        case 'music':
            const audioData = audioUtils.extractModelDataFromSrc(url);
            return {
                url,
                mediaId: audioData.mediaId,
                title: audioData.title,
                duration: audioData.duration
            };
        default:
            throw new TypeError(`Unknown media_type "${mediaType}"`);
    }
}

/**
 * @class UploadButton
 * @summary An upload button enables site visitors to upload files to your site.
 * @description
 *  ### Typical File Upload Scenario
 * 
 *  In a typical scenario, the page from which files are uploaded contains
 *  an upload button and another element, such as a regular button. 
 * 
 *  1. The site visitor chooses which file to upload by clicking the upload button and selecting the file in a native file chooser dialog. 
 *  1. At that point the file is stored in the upload button's `value` property as a `File` object. 
 *  1. The `onChange()` event is fired.
 *  1. Validations on the file are performed, following which the `valid`, `validity`, and `validationMessage` properties are updated. Because `onChange()` is fired before validations are performed, do not check any of the validation properties in the code of the `onChange()` event handler. For example, do not code an `if` statement that checks the `valid` property in the `onChange()` because the property at this point contains values prior to the `onChange()`.
 *  1. The site visitor triggers an event, such as a button click, on the other element. 
 *  1. That element's event handler calls the `startUpload()` function to perform the actual upload. 
 *  1. The upload either succeeds and returns an `UploadedFile` object, or fails and returns an `UploadError` object.
 * 
 * 
 * 
 * 
 * 
 * 
 *  ### Example
 * 
 *  This example uploads a file when the site visitor clicks on a button. First it sets 
 *  the type of the file a site visitor can choose. Then the example
 *  checks to see if the site visitor has chosen a file. If a file was chosen, it
 *  triggers the upload and logs the status of the upload when it completes
 *  successfully or with an error.
 * 
 * 
 *    ``` javascript
 *    $w("#myUploadButton").fileType = "Image";        // visitor can choose an image only
 *    $w('#myButton').onClick( () => {                 // visitor clicks a regular, non-upload button 
 *      if ($w("#myUploadButton").value.length > 0) {  // visitor chose a file
 *          console.log("Uploading " + $w("#myUploadButton").value[0].name + "- Please wait.");
 *          $w("#myUploadButton").startUpload()
 *            .then( (uploadedFile) => {
 *              console.log("Upload successful. File is available here:");
 *              console.log(uploadedFile.url);
 *            } )
 *            .catch( (uploadError) => {
 *              console.log("File upload error: " + uploadError.errorCode);
 *              console.log(uploadError.errorDescription);
 *            } );
 *      }
 *      else {    // site visitor clicked button but didn't choose a file
 *        console.log("Please choose a file to upload.")
 *      }
 *    } );
 *    ```
 * 
 * 
 * 
 * > **Note:** 
 * > The `onChange()` event is fired before validation. As a result, consider the following points if you intend
 *   to call the `startUpload()` function inside the `onChange()` event handler: 
 * > + `startUpload()` returns promptly with an `UploadError` object if the `fileSizeExceedsLimit valid` property or the `fileSizeExceedsLimit validity` property is true. 
 * > + If the validation fails for one of these two reasons, causing an error to be returned, the `startUpload()` does not attempt the upload.
 * @mixes $w.FormElement
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.DisabledMixin
 * @mixes $w.FocusMixin
 * @mixes $w.StyleMixin
 * @mixes $w.RequiredMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.inputs.FileUploader
 * @definitionId wysiwyg.viewer.components.inputs.FileUploader
 * @tagname fileuploader
 */
class UploadButton extends FormElement {

    // PROPERTIES:

    /**
     * @member buttonLabel
     * @label property
     * @summary Sets or gets the label on the upload button.
     * @type {String}
     * @snippet [UploadButton-buttonLabel_get.es6=Get an upload button's label]
     * @snippet [UploadButton-buttonLabel_set.es6=Set an upload button's label]
     * @memberof $w.UploadButton
     * @instance
     */
    get buttonLabel() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId);

        return compData && compData.buttonLabel;
    }


  /**
   * @member buttonLabel
   * @memberof $w.UploadButton
   * @param {String} label
   */
    set buttonLabel(label) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'buttonLabel',
            dataToValidate: {buttonLabel: label},
            compName: this.id
        })) {
            return;
        }
        RMI.setData(compId, {
            buttonLabel: emptyStringIfNil(label)
        });
    }

    /**
     * @member fileType
     * @label property
     * @summary Sets or gets the type of the file that a site visitor can upload.
     * @description
     *  Setting the `fileType` property sets the type of the file that
     *  a site visitor can upload. The value of `fileType` can be set to 
     *  `"Image"`, `"Document"`, `"Video"`, or `"Audio"`.
     *
     *  Getting the `fileType` property returns the current type of
     *  file that a site visitor can upload.
     *
     *  The file types that can be uploaded are
     *  listed [here](https://support.wix.com/en/article/supported-media-file-types-and-file-sizes).
     * @snippet [UploadButton-fileType_get.es6=Get an upload button's type]
     * @snippet [UploadButton-fileType_set.es6=Set an upload button's type]
     * @type {String}
     * @memberof $w.UploadButton
     */
    get fileType() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compProps = RMI.getProps(compId);

        return compProps && compProps.filesType;
    }

  /**
   * @member fileType
   * @memberof $w.UploadButton
   * @param {String} type
   */
    set fileType(type) {
        type = _.isString(type) ? _.startCase(type) : type;
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'fileType',
            dataToValidate: {filesType: type},
            shouldCheckMandatoryFields: true,
            compName: this.id
        })) {
            return;
        }
        RMI.setProps(compId, {
            filesType: type
        });


    }

    /**
     * @member value
     * @label property
     * @summary Returns a list of files that are pending upload.
     * @description
     *  Returns a list of `File` objects that a site visitor has selected to
     *  upload after pressing the upload button.
     *
     *  See how `value` is used in a
     *  [typical file upload scenario]($w/uploadbutton/introduction#$w_uploadbutton_introduction_typical-file-upload-scenario).
     * @snippet [UploadButton-value.es6=Get the files pending upload]
     * @snippet [UploadButton-startUpload_scenario.es6=Full file upload scenario]
     *  This example uploads a file when the site visitor clicks on a button. First it sets 
     *  the type of the file a site visitor can choose. Then the example
     *  checks to see if the site visitor has chosen a file. If a file was chosen, it
     *  triggers the upload and logs the status of the upload when it completes
     *  successfully or with an error.
     * @type {$w.UploadButton~File[]}
     * @memberof $w.UploadButton
     * @readonly
     * @override
     */
    get value() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compState = RMI.getState(compId);

        return _.get(compState, 'value');
    }


    set value(val) { // eslint-disable-line no-unused-vars
        const compType = members._type.get(this).compType;
        reporter.reportSdkError(generateError.error_only_getter({
            propertyName: 'value',
            compType
        }));
    }

    /**
     * @function startUpload
     * @summary Uploads the files that the site visitor has chosen.
     * @description
     *  The `startUpload()` function triggers the upload of the files in the
     *  upload button's [value](#value) property.
     *
     *  See how `startUpload()` is used in a
     *  [typical file upload scenario]($w/uploadbutton/introduction#$w_uploadbutton_introduction_typical-file-upload-scenario).
     *
     *  The upload button does not accept files larger than certain sizes, depending on the file type. Maximum allowed file sizes are listed [here](https://support.wix.com/en/article/supported-media-file-types-and-file-sizes).
     * 
     *  `startUpload()` returns promptly (meaning, it does not attempt to contact the upload server) with `UploadError` when either the `validityState`'s `fileSizeExceedsLimit` or `fileTypeNotAllowed` property is `true`.
     * 
     * @returns {Promise}
     * @fulfill {$w.UploadButton~UploadedFile} Resolves when the file upload is completed and returns an `UploadedFile` object.
     * @reject {$w.UploadButton~UploadError} Rejects if the file upload fails and returns an `UploadError` object, which contains the error code and description.
     * @snippet [UploadButton-startUpload.es6=Start a file upload]
     * @snippet [UploadButton-startUpload_scenario.es6=Full file upload scenario]
     *  This example uploads a file when the site visitor clicks on a button. First it sets 
     *  the type of the file a site visitor can choose. Then the example
     *  checks to see if the site visitor has chosen a file. If a file was chosen, it
     *  triggers the upload and logs the status of the upload when it completes
     *  successfully or with an error.
     * @memberof $w.UploadButton
     */
    startUpload() {
        const compId = members._compId.get(this);
        return new Promise((resolve, reject) => {
            const callback = (data, err) => {
                if (err) {
                    reject(err);
                } else {
                    try {
                        const url = getWixCodeURI(data);
                        resolve(getFileInfo(data.media_type, url));
                    } catch (error) {
                        if (_.startsWith(error.message, 'Unknown media_type')) {
                            resolve(data);
                        }
                        throw error;
                    }
                }
            };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.START_UPLOAD,
                data: {compId},
                controllerId: members._controllerId.get(this),
                callback
            });
        });
    }

    /**
     * @function reset
     * @summary Clears the files that are pending upload.
     * @description
     *  The [`value`](#value) of the upload button contains a list of files that have been
     *  selected to upload. The `reset()` function clears the files in `value`.
     * @snippet [UploadButton-reset.es6=Clears the files pending upload]
     * @memberof $w.UploadButton
     */
    reset() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const value = [];
        const newState = _.defaults({value, uploadStatus: 'NOT_STARTED', serverMessage: ''}, RMI.getState(compId));

        RMI.updateModel({[compId]: {state: newState}});
        RMI.executeCompBehavior(compId, 'reset');
        runValidationsAndSync(RMI, compId, value); // must run validations after executing rest behavior so that viewer validations will be correct
        hideValidityIndication(RMI, compId);
    }

    toJSON() {
        const {id, type, global, rendered, required, value, buttonLabel, fileType, startUpload, reset} = this;
        return {id, type, global, rendered, required, value, buttonLabel, fileType, startUpload, reset};
    }

    /**
     * @member style
     * @label property
     * @summary Gets an object containing information about the upload button's styles.
     * @description
     *  The following styles can be used with upload buttons:
     *
     *  + [`backgroundColor`]($w.Style.html#backgroundColor)
     *  + [`borderColor`]($w.Style.html#borderColor)
     *  + [`borderRadius`]($w.Style.html#borderRadius)
     *  + [`borderWidth`]($w.Style.html#borderWidth)
     *  + [`color`]($w.Style.html#color)
     *
     *
     *  Getting or setting an upload button's styles, gets or sets the styles of the
     *  upload button's regular state. It does not set the styles of the upload button's hover,
     *  focus, error, or disabled states.
     * @snippet [BackgroundColorMixin-backgroundColor_set.es6=Set the background color]
     * @snippet [BackgroundColorMixin-backgroundColor_get.es6=Get the background color]
     * @type {$w.Style}
     * @memberof $w.UploadButton
     * @readonly
     * @override
     */

   /**
     * @member valid
     * @label property
     * @summary Indicates if a required file was selected, and if the file is valid for upload to the Media Manager.
     * @description
     *  The `valid` property indicates if the validation passed. `valid` is `false` if, for example:
     * 
     *  
     *  + A file was not selected, in cases where the file is required.
     *  + A file's type is not supported.
     *  + A file's size is not within the allowed limit.
     *  + There was a failed attempt to upload a file that passed input validation checks to the Media Manager with `startUpload()`. This could happen, for example, if the Internet connection is not stable. In this case, the `fileNotUploaded` `validity` property is also set to false.
     * 
     * 
     * 
     * 
     * @snippet [FileUpload-valid.es6=Get whether the file is valid for upload]
     * @type {Boolean}
     * @memberof $w.UploadButton
     * @readonly
     * @override
     */


    /**
     * @member validationMessage
     * @label property
     * @summary Gets a message indicating why the file is invalid or could not be uploaded. Contains
     *  empty string if the file is valid and successfully uploaded.
     * @description
     *  Set the value of the `validationMessage` property using the
     *  [`reject()`](#reject) function of the [`onCustomValidation()`](#onCustomValidation)
     *  event handler.
     * 
     *  The message indicates why a file was not valid, or why a file could not be uploaded to the Media Manager. (This could happen, for example, if the Internet connection is not stable.)
     * @snippet [ValidatableMixin-validationMessage.es6=Get the validation message]
     * @type {String}
     * @see [validity](#validity)
     * @memberof $w.UploadButton
     * @readonly
     * @override
     */


    /**
     * @member validity
     * @label property
     * @summary Gets a ValidityState object that contains
     *  detailed information about the validity states of the file to upload.
     * @snippet [ValidityStateFileUpload.es6=Log ValidityState info]
     * @type {$w.UploadButton~ValidityState}
     * @see [validationMessage](#validationMessage)
     * @memberof $w.UploadButton
     * @readonly
     * @override
     */

    /**
     * @typedef {Object} $w.UploadButton~ValidityState
     * @summary Contains detailed information about the validity states of a file to upload. 
     * @description
     *  States not related to file uploads can be ignored.
     * @property {Boolean} customError - `true` if the file's custom validity message has been set to a non-empty string.
     * @property {Boolean} valid - `true` if the file to upload meets all validations.
     * @property {Boolean} valueMissing - `true` if a file is required but no file has been selected.
     * @property {Boolean} fileNotUploaded - `true` if a file has been selected but not uploaded.
     * @property {Boolean} fileSizeExceedsLimit - `true` if a file has been selected but it is too large. Maximum allowed file sizes are listed [here](https://support.wix.com/en/article/supported-media-file-types-and-file-sizes).
     * @property {Boolean} fileTypeNotAllowed - `true` if a file has been selected but the file's type is not supported. Supported file types are listed [here](https://support.wix.com/en/article/supported-media-file-types-and-file-sizes).
     * @property {external:Boolean} typeMismatch - `true` if the text input element's value does not match its type when the type is email or url.
     * @property {external:Boolean} patternMismatch - `true` if the element's value does not match its pattern validation.
     * @property {external:Boolean} tooLong - `true` if the length of the element's value exceeds its [`maxLength`]($w.TextInputMixin.html#maxLength) property.
     * @property {external:Boolean} tooShort - `true` if the length of the element's value is smaller than its `minlength` property.
     * @property {external:Boolean} rangeUnderflow - `true` if the element's value is less than the its [`min`]($w.TextInput.html#min) property.
     * @property {external:Boolean} rangeOverflow - `true` if the element's value is more than the its [`max`]($w.TextInput.html#max) property.
     * @property {external:Boolean} stepMismatch - `true` if a text input element's numeric value is defined as a whole number but a decimal number is entered.
     * @property {external:Boolean} badInput - `true` if the element's value cannot be converted to a value.




     * @snippet [ValidityStateFileUpload.es6=Get ValidityState info]
     * @see [validity](#validity)
     * @override
     */


}

mixin(UploadButton.prototype, HiddenCollapsedMixin.prototype);
mixin(UploadButton.prototype, DisabledMixin.prototype);
mixin(UploadButton.prototype, FocusMixin.prototype);
mixin(UploadButton.prototype, StyleMixin.prototype);
mixin(UploadButton.prototype, RequiredMixin.prototype);

setStageUnfriendlyFunctions(UploadButton, 'startUpload');
export default UploadButton;
